import { useState } from "react";
import { useIntl } from "react-intl";

import { ClockIcon } from "@radix-ui/react-icons";

import { CarouselItem } from "~/hec/components/ui/carousel";
import { Heading } from "~/hec/components/ui/heading";

import GoalAnswer from "./goal-answer";
import GoalResult from "./goal-result";
import messages from "./messages";
import type { GoalData, GoalEntry, QuestionData } from "./types";

/**
 * GoalQuestion — one question slide (SFCC-11290). Ported from composable
 * `goalQuestion.jsx`. Rendered as a `CarouselItem` so the questionnaire animates
 * between slides (SFCC-11730). Left column: the question + answers (or a Back
 * control on non-first questions). Right column (desktop): the question image, OR
 * the recommended-goal panel once a goal is reached on the last slide. On mobile
 * the goal panel renders below the carousel (owned by GoalQuestionnaire).
 */
interface GoalQuestionProps {
  question: QuestionData;
  isFirst: boolean;
  isLast: boolean;
  goal: GoalData | null;
  isMobile: boolean;
  isGuest: boolean;
  goals: GoalEntry[];
  onSelectQuestion: (next: QuestionData) => void;
  onSelectGoal: (goal: GoalData) => void;
  onAdvance: () => void;
  onBack: () => void;
}

const GoalQuestion = ({
  question,
  isFirst,
  isLast,
  goal,
  isMobile,
  isGuest,
  goals,
  onSelectQuestion,
  onSelectGoal,
  onAdvance,
  onBack,
}: GoalQuestionProps) => {
  const { question: questionTitle, image, answers } = question;
  const [selectedValue, setSelectedValue] = useState("");
  const intl = useIntl();

  // No-answers guard (SFCC-11724): a malformed Builder question with no answers
  // renders nothing — a defined empty state. The composable logged a
  // `console.error` here; that surfaces in the production console on every such
  // entry, so it's dropped (the AC requires no console error in the prod build).
  // The empty render is the parity-correct outcome — there's nothing to display.
  if (!answers || answers.length === 0) {
    return null;
  }

  return (
    <CarouselItem
      className="flex items-start justify-center gap-8 px-4 py-4 text-primary sm:px-8"
      data-testid="goalQuestion-CarouselItem"
    >
      <div className="w-full flex-1 sm:max-w-[50%]">
        {isFirst ? (
          <div className="flex items-center space-x-2 text-sm">
            <ClockIcon className="h-4 w-4" />
            <span className="mt-1 text-sm">
              {intl.formatMessage(messages.estimatedTime)}
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={onBack}
            className="cursor-pointer text-sm underline"
            data-testid="goalQuestion-Back"
          >
            {intl.formatMessage(messages.back)}
          </button>
        )}
        <Heading
          tag="h4"
          variant="h4"
          className="my-4"
          data-testid="goalQuestion-title"
        >
          {questionTitle}
        </Heading>
        <div role="radiogroup">
          {answers.map((answer, answerId) => (
            <GoalAnswer
              key={`answer-${answerId}`}
              answer={answer}
              answerId={answerId}
              selectedValue={selectedValue}
              setSelectedValue={setSelectedValue}
              goals={goals}
              onSelectQuestion={onSelectQuestion}
              onSelectGoal={onSelectGoal}
              onAdvance={onAdvance}
            />
          ))}
        </div>
      </div>
      <div className="hidden max-w-[40%] flex-1 items-center justify-center sm:flex">
        {/* Desktop-only mount: the mobile GoalResult is rendered by
            GoalQuestionnaire below the stepper. Gating on `!isMobile` (not just
            the CSS `hidden`) guarantees a single GoalResult instance, so the
            post-login resume handshake can't be armed twice. */}
        {goal && isLast && !isMobile ? (
          <GoalResult goal={goal} isMobile={isMobile} isGuest={isGuest} />
        ) : (
          image && <img src={image} alt={questionTitle} />
        )}
      </div>
    </CarouselItem>
  );
};

export default GoalQuestion;
